import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { 
  BarChart3, Users, GraduationCap, MapPin, Download, 
  TrendingUp, Award, Calendar, FileText
} from 'lucide-react';

export default function PartnerDashboard() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [overviewReport, setOverviewReport] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [dashboardRes, overviewRes] = await Promise.all([
        api.get('/partner/dashboard'),
        api.get('/partner/reports/overview')
      ]);
      setDashboardData(dashboardRes.data);
      setOverviewReport(overviewRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (reportType) => {
    try {
      const response = await api.get(`/partner/reports/export?type=${reportType}&format=json`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_report.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const stats = [
    { label: 'Total Courses', value: dashboardData?.total_courses || 0, icon: GraduationCap, color: 'text-blue-600' },
    { label: 'Active Sections', value: dashboardData?.total_sections || 0, icon: Calendar, color: 'text-green-600' },
    { label: 'Students Enrolled', value: dashboardData?.total_students || 0, icon: Users, color: 'text-purple-600' },
    { label: 'Total Enrollments', value: dashboardData?.total_enrollments || 0, icon: FileText, color: 'text-orange-600' },
    { label: 'Completion Rate', value: `${dashboardData?.completion_rate || 0}%`, icon: TrendingUp, color: 'text-teal-600' },
    { label: 'Satisfaction', value: `${dashboardData?.satisfaction_score || 0}/5`, icon: Award, color: 'text-yellow-600' },
  ];

  return (
    <div className="space-y-8" data-testid="partner-dashboard">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold mb-2">Partner Portal</h1>
        <p className="text-muted-foreground">
          Welcome back, {user?.first_name}! View program metrics and generate reports.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {stats.map((stat, index) => (
          <Card key={index} className="card-dashboard">
            <CardContent className="p-4 text-center">
              <stat.icon className={`h-8 w-8 mx-auto mb-2 ${stat.color}`} />
              <p className="text-2xl font-bold">{stat.value}</p>
              <p className="text-xs text-muted-foreground">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Enrollment Trend */}
      {overviewReport?.enrollment_trend && (
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Enrollment Trend
            </CardTitle>
            <CardDescription>Monthly enrollment numbers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-4 h-48">
              {overviewReport.enrollment_trend.map((item, index) => (
                <div key={index} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full bg-primary rounded-t"
                    style={{ height: `${(item.count / 100) * 150}px` }}
                  />
                  <span className="text-xs mt-2 font-medium">{item.month}</span>
                  <span className="text-xs text-muted-foreground">{item.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Course Distribution */}
      {overviewReport?.course_distribution && (
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle>Course Distribution</CardTitle>
            <CardDescription>Enrollment by course category</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {overviewReport.course_distribution.map((item, index) => (
                <div key={index} className="flex items-center gap-4">
                  <span className="w-24 text-sm font-medium">{item.category}</span>
                  <div className="flex-1 bg-secondary rounded-full h-4 overflow-hidden">
                    <div 
                      className="bg-primary h-full rounded-full"
                      style={{ width: `${(item.count / 150) * 100}%` }}
                    />
                  </div>
                  <span className="w-12 text-sm text-right">{item.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export Reports */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Export Reports
          </CardTitle>
          <CardDescription>Download detailed reports for analysis</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button variant="outline" onClick={() => handleExport('overview')} data-testid="export-overview">
              <FileText className="h-4 w-4 mr-2" />
              Overview
            </Button>
            <Button variant="outline" onClick={() => handleExport('locations')} data-testid="export-locations">
              <MapPin className="h-4 w-4 mr-2" />
              Locations
            </Button>
            <Button variant="outline" onClick={() => handleExport('courses')} data-testid="export-courses">
              <GraduationCap className="h-4 w-4 mr-2" />
              Courses
            </Button>
            <Button variant="outline" onClick={() => handleExport('demographics')} data-testid="export-demographics">
              <Users className="h-4 w-4 mr-2" />
              Demographics
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
